import { botResponse$ } from "./chatStream";
import store from "../../../../app/store";
import moment from "moment";
import {
  addStreamingBotMessage,
  appendStreamingChunk,
  completeStreamingBotMessage,
  setExistingThreadID,
  setExistingThreadName,
  setIsReponseDone,
  setStreaming,
  setThreadDetails,
} from "../../../../rtk-query/features/chatbot/chatbotSlice";
import { fetchThreadList } from "../../../../rtk-query/features/chatbot/chatbotSidebarToolsSlice";

let isSubscribed = false;
let subscription = null;

let currentBotMessageId = null;
let metadata = null;

// ✅ ONLY ONE SOURCE OF TRUTH
// Typing queue
let typingQueue = "";
let typingTimer = null;
let responseFinished = false;
let streamedMarkdown = "";
const RESPONSE_DONE_MARKER = "[RESPONSE_DONE]";
const DONE_MARKER = "[DONE]";

function startTyping() {
  if (typingTimer || !currentBotMessageId) return;

  typingTimer = setInterval(() => {
    if (!typingQueue.length) {
      clearInterval(typingTimer);
      typingTimer = null;

      // API has finished and all queued text has been rendered.
      if (responseFinished) {
        // store.dispatch(setStreaming(false));
        responseFinished = false;
      }

      return;
    }

    // Scale reveal size with the backlog so a growing queue drains faster
    // instead of lagging further behind the incoming stream.
    const size = Math.max(4, Math.ceil(typingQueue.length / 8));

    let end = size;

    while (
      end < typingQueue.length &&
      !/[\s.,!?;:`*|)/]/.test(typingQueue[end])
    ) {
      end++;
    }

    const chunk = typingQueue.slice(0, end);
    typingQueue = typingQueue.slice(end);

    store.dispatch(
      appendStreamingChunk({
        id: currentBotMessageId,
        chunk,
      }),
    );
  }, 16);
}

function scheduleChunk(chunk) {
  typingQueue += chunk;
  startTyping();
}

//Old code
// function parseChunk(rawData) {
//   const lines = rawData.split("\n");

//   let result = "";

//   for (const line of lines) {
//     const trimmed = line.trimStart();

//     // Handle SSE data lines
//     if (line.startsWith("data:")) {
//       const content = line.replace(/^data:\s?/, "");

//       // Ignore empty SSE events (e.g. "data:")
//       if (!content.trim()) {
//         continue;
//       }

//       result += content;
//       continue;
//     }

//     // Horizontal rule
//     if (trimmed === "---") {
//       result += "\n\n---\n\n";
//     }
//     // Code block
//     else if (trimmed === "```") {
//       result += "\n```\n";
//     }
//     // Numbered list after bold heading
//     else if (result.endsWith("**") && /^[0-9]/.test(trimmed)) {
//       result += "\n\n" + line;
//     }
//     // Markdown headings / bold
//     else if (trimmed.startsWith("**") || trimmed.startsWith("#")) {
//       result += "\n\n" + trimmed;
//     }
//     // New paragraph for capitalized sentence
//     else if (/^[A-Z]/.test(trimmed)) {
//       result += "\n\n" + trimmed;
//     }
//     // Lists / tables / code
//     else if (
//       line.startsWith("- ") ||
//       line.startsWith("* ") ||
//       line.startsWith("|") ||
//       line.startsWith("```") ||
//       line.startsWith("✓") ||
//       /^\d+\.\s/.test(trimmed) ||
//       /^\d+$/.test(trimmed)
//     ) {
//       result += "\n" + line;
//     }
//     // Everything else
//     else {
//       result += line;
//     }
//   }

//   return result;
// }

export function appendSSEContent(result, content) {
  if (content) {
    const requiredBreak = getBlockBreak(result, content);

    return requiredBreak && !result.endsWith(requiredBreak)
      ? result + requiredBreak + content
      : result + content;
  }

  const currentLine = result.slice(result.lastIndexOf("\n") + 1).trimStart();
  const lineBreak =
    currentLine.startsWith("|") || currentLine.startsWith("-") ? "\n" : "\n\n";

  return result.endsWith(lineBreak) ? result : result + lineBreak;
}

function getBlockBreak(result, content) {
  const marker = content.trimStart();
  const currentLine = result.slice(result.lastIndexOf("\n") + 1).trim();

  if (/^#{1,6}$/.test(marker) || /^#{1,6}\s/.test(marker)) {
    return currentLine ? "\n\n" : "";
  }

  return "";
}

function parseChunk(rawData, previousContent = "") {
  const lines = rawData.split("\n");
  let result = previousContent;

  for (const line of lines) {
    if (line.startsWith("data:")) {
      // SSE spec: exactly one separator space after "data:" is not part of the payload.
      const rawContent = line.slice(5);
      const content = rawContent.startsWith(" ")
        ? rawContent.slice(1)
        : rawContent;

      result = appendSSEContent(result, content);
      continue;
    }

    const nonDataContent = line.trim();
    if (!nonDataContent) continue;

    // Keep transport/control markers flowing through when provided outside data: lines.
    // Append the original (untrimmed) line so incidental whitespace isn't lost.
    if (
      nonDataContent.startsWith(DONE_MARKER) ||
      nonDataContent.startsWith(RESPONSE_DONE_MARKER) ||
      (nonDataContent.startsWith("{") && nonDataContent.endsWith("}"))
    ) {
      result = appendSSEContent(result, line);
    }
  }

  return result.slice(previousContent.length);
}

export const initChatStreamListener = () => {
  if (isSubscribed) return;

  isSubscribed = true;

  const handleResponseDone = (value) => {
    value = value.replace(RESPONSE_DONE_MARKER, "");

    if (value.trim()) {
      ensureCurrentBotMessage();
      scheduleChunk(value);
    }

    responseFinished = true;
  };

  const finishStreaming = () => {
    store.dispatch(
      completeStreamingBotMessage({
        id: currentBotMessageId,
        feedbackId: metadata?.Id,
        isLike: metadata?.isLike,
      }),
    );

    if (metadata) {
      store.dispatch(
        setThreadDetails({
          threadId: metadata.ThreadId,
          threadName: metadata.ThreadName,
        }),
      );

      Promise.resolve(store.dispatch(fetchThreadList())).catch((error) => {
        console.error("Failed to refresh chat threads:", error);
      });
      store.dispatch(setExistingThreadID(metadata.ThreadId));
      store.dispatch(setExistingThreadName(metadata.ThreadName));
      store.dispatch(setStreaming(false));
    }

    store.dispatch(setIsReponseDone(false));

    currentBotMessageId = null;
    metadata = null;
    typingQueue = "";
    streamedMarkdown = "";
    responseFinished = false;
  };

  const handleDone = (value) => {
    const responseValue = value.replace(DONE_MARKER, "");

    if (responseValue.trim()) {
      ensureCurrentBotMessage();
      scheduleChunk(responseValue);
    }

    responseFinished = true;

    try {
      if (typingQueue.length || typingTimer) {
        const wait = setInterval(() => {
          if (!typingQueue.length && !typingTimer) {
            clearInterval(wait);
            finishStreaming();
          }
        }, 30);
      } else {
        finishStreaming();
      }
    } catch (e) {
      console.error("Error completing stream:", e);
    }
  };

  const parseMetadata = (value) => {
    const start = value.indexOf("{");
    const end = value.lastIndexOf("}");

    if (start === -1 || end <= start) {
      return false;
    }

    try {
      metadata = JSON.parse(value.slice(start, end + 1));
    } catch (e) {
      console.error("Metadata parse error", value, e);
    } finally {
      store.dispatch(setIsReponseDone(false));
    }

    return true;
  };

  const ensureCurrentBotMessage = () => {
    if (currentBotMessageId) {
      return;
    }

    currentBotMessageId = `${Date.now()}-${crypto.getRandomValues(new Uint32Array(1))[0]}`;

    store.dispatch(
      addStreamingBotMessage({
        id: currentBotMessageId,
        dateTime: moment(),
      }),
    );
  };

  subscription = botResponse$.subscribe((event) => {
    const rawData = event?.data;
    if (!rawData) return;

    const value = parseChunk(rawData, streamedMarkdown);

    if (!value) return;

    streamedMarkdown += value;

    if (value.includes(RESPONSE_DONE_MARKER)) {
      handleResponseDone(value);
      return;
    }

    if (value.includes(DONE_MARKER)) {
      handleDone(value);
      return;
    }

    if (parseMetadata(value)) {
      return;
    }

    ensureCurrentBotMessage();
    scheduleChunk(value);
  });
};

export const destroyChatStreamListener = () => {
  if (typingTimer) {
    clearInterval(typingTimer);
    typingTimer = null;
  }

  typingQueue = "";
  streamedMarkdown = "";
  responseFinished = false;

  if (subscription) {
    subscription.unsubscribe();
    subscription = null;
  }

  isSubscribed = false;
};
